/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/* Geodesy tools for an ellipsoidal earth model                       (c) Chris Veness 2005-2016  */
/*                                                                                   MIT Licence  */
/* www.movable-type.co.uk/scripts/latlong-convert-coords.html                                     */
/* www.movable-type.co.uk/scripts/geodesy/docs/module-latlon-ellipsoidal.html                     */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

import {Dms} from "App/utils/projections/dms";
import {Vector3d} from "App/utils/projections/vector_3d";

'use strict';

/**
 * Library of geodesy functions for operations on an ellipsoidal earth model.
 *
 * Includes ellipsoid parameters and datums for different coordinate systems, and methods for
 * converting between them and to cartesian coordinates.
 *
 * q.v. Ordnance Survey ‘A guide to coordinate systems in Great Britain’ Section 6
 * www.ordnancesurvey.co.uk/docs/support/guide-coordinate-systems-great-britain.pdf.
 *
 * @module   latlon-ellipsoidal
 * @requires dms
 */


/**
 * Creates lat/lon (polar) point with latitude & longitude values, on a specified datum.
 *
 * @constructor
 * @param {number}       lat - Geodetic latitude in degrees.
 * @param {number}       lon - Longitude in degrees.
 * @param {LatLon.datum} [datum=WGS84] - Datum this point is defined within.
 *
 * @example
 *     let p1 = new LatLon(51.4778, -0.0016, LatLon.datum.WGS84);
 */

export class LatLon {

    /**
     * Ellipsoid parameters; major axis (a), minor axis (b), and flattening (f) for each ellipsoid.
     */
    public static ellipsoid = {
        WGS84: {a: 6378137, b: 6356752.314245, f: 1 / 298.257223563},
        Airy1830: {a: 6377563.396, b: 6356256.909, f: 1 / 299.3249646},
        AiryModified: {a: 6377340.189, b: 6356034.448, f: 1 / 299.3249646},
        Bessel1841: {a: 6377397.155, b: 6356078.962818, f: 1 / 299.1528128},
        Clarke1866: {a: 6378206.4, b: 6356583.8, f: 1 / 294.978698214},
        Clarke1880IGN: {a: 6378249.2, b: 6356515.0, f: 1 / 293.466021294},
        GRS80: {a: 6378137, b: 6356752.314140, f: 1 / 298.257222101},
        Intl1924: {a: 6378388, b: 6356911.946, f: 1 / 297}, // aka Hayford
        WGS72: {a: 6378135, b: 6356750.5, f: 1 / 298.26},
    };

    /**
     * Datums; with associated ellipsoid, and Helmert transform parameters to convert from WGS 84 into
     * given datum.
     *
     * Note that precision of letious datums will lety, and WGS-84 (original) is not defined to be
     * accurate to better than ±1 metre. No transformation should be assumed to be accurate to better
     * than a meter; for many datums somewhat less.
     */
    public static datum = {
        // transforms: t in metres, s in ppm, r in arcseconds                    tx       ty        tz       s        rx       ry       rz
        ED50: {ellipsoid: LatLon.ellipsoid.Intl1924, transform: [89.5, 93.8, 123.1, -1.2, 0.0, 0.0, 0.156]},
        Irl1975: {
            ellipsoid: LatLon.ellipsoid.AiryModified,
            transform: [-482.530, 130.596, -564.557, -8.150, -1.042, -0.214, -0.631]
        },
        NAD27: {ellipsoid: LatLon.ellipsoid.Clarke1866, transform: [8, -160, -176, 0, 0, 0, 0]},
        NAD83: {
            ellipsoid: LatLon.ellipsoid.GRS80,
            transform: [1.004, -1.910, -0.515, -0.0015, 0.0267, 0.00034, 0.011]
        },
        NTF: {ellipsoid: LatLon.ellipsoid.Clarke1880IGN, transform: [168, 60, -320, 0, 0, 0, 0]},
        OSGB36: {
            ellipsoid: LatLon.ellipsoid.Airy1830,
            transform: [-446.448, 125.157, -542.060, 20.4894, -0.1502, -0.2470, -0.8421]
        },
        Potsdam: {ellipsoid: LatLon.ellipsoid.Bessel1841, transform: [-582, -105, -414, -8.3, 1.04, 0.35, -3.08]},
        TokyoJapan: {ellipsoid: LatLon.ellipsoid.Bessel1841, transform: [148, -507, -685, 0, 0, 0, 0]},
        WGS72: {ellipsoid: LatLon.ellipsoid.WGS72, transform: [0, 0, -4.5, -0.22, 0, 0, 0.554]},
        WGS84: {ellipsoid: LatLon.ellipsoid.WGS84, transform: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0]},
    };

    /* sources:
     * - ED50:          www.gov.uk/guidance/oil-and-gas-petroleum-operations-notices#pon-4
     * - Irl1975:       www.osi.ie/wp-content/uploads/2015/05/transformations_booklet.pdf
     *   ... note: many sources have opposite sign to rotations - to be checked!
     * - NAD27:         en.wikipedia.org/wiki/Helmert_transformation
     * - NAD83: (2009); www.uvm.edu/giv/resources/WGS84_NAD83.pdf
     *   ... note: functionally ≡ WGS84 - if you *really* need to convert WGS84<->NAD83, you need more knowledge than this!
     * - NTF:           Nouvelle Triangulation Francaise geodesie.ign.fr/contenu/fichiers/Changement_systeme_geodesique.pdf
     * - OSGB36:        www.ordnancesurvey.co.uk/docs/support/guide-coordinate-systems-great-britain.pdf
     * - Potsdam:       kartoweb.itc.nl/geometrics/Coordinate%20transformations/coordtrans.html
     * - TokyoJapan:    www.geocachingtoolbox.com?page=datumEllipsoidDetails
     * - WGS72:         www.icao.int/safety/pbn/documentation/eurocontrol/eurocontrol wgs 84 implementation manual.pdf
     *
     * more transform parameters are available from earth-info.nga.mil/GandG/coordsys/datums/NATO_DT.pdf,
     * www.fieldenmaps.info/cconv/web/cconv_params.js
     */

    public lat: number;
    public lon: number;
    public _datum: any;

    constructor(lat: number, lon: number, datum: any) {
        // allow instantiation without 'new'
        if (!(this instanceof LatLon)) return new LatLon(lat, lon, datum);

        if (datum === undefined) datum = datum.WGS84;

        this.lat = Number(lat);
        this.lon = Number(lon);
        this._datum = datum;
    }

    /**
     * Converts ‘this’ lat/lon coordinate to new coordinate system.
     *
     * @param   {LatLon.datum} toDatum - Datum this coordinate is to be converted to.
     * @returns {LatLon} This point converted to new datum.
     *
     * @example
     *     let pWGS84 = new LatLon(51.4778, -0.0016, LatLon.datum.WGS84);
     *     let pOSGB = pWGS84.convertDatum(LatLon.datum.OSGB36); // 51.4773°N, 000.0000°E
     */
    public convertDatum(toDatum: any): any {
        let oldLatLon = this;
        let transform = null;

        if (oldLatLon._datum == LatLon.datum.WGS84) {
            // converting from WGS 84
            transform = toDatum.transform;
        }
        if (toDatum == LatLon.datum.WGS84) {
            // converting to WGS 84; use inverse transform (don't overwrite original!)
            transform = [];
            for (let p = 0; p < 7; p++) transform[p] = -oldLatLon._datum.transform[p];
        }
        if (transform == null) {
            // neither this._datum nor toDatum are WGS84: convert this to WGS84 first
            oldLatLon = this.convertDatum(LatLon.datum.WGS84);
            transform = toDatum.transform;
        }

        let oldCartesian = oldLatLon.toCartesian();                // convert polar to cartesian...
        let newCartesian = oldCartesian.applyTransform(transform); // ...apply transform...
        let newLatLon = LatLon.toLatLonE(newCartesian, toDatum);           // ...and convert cartesian to polar

        return newLatLon;
    }

    /**
     * Converts ‘this’ point from (geodetic) latitude/longitude coordinates to (geocentric) cartesian
     * (x/y/z) coordinates.
     *
     * @returns {Vector3d} Vector pointing to lat/lon point, with x, y, z in metres from earth centre.
     */
    public toCartesian(): Vector3d {
        let φ = LatLon.toRadians(this.lat), λ = LatLon.toRadians(this.lon);
        let h = 0; // height above ellipsoid - not currently used
        let a = this._datum.ellipsoid.a, f = this._datum.ellipsoid.f;

        let sinφ = Math.sin(φ), cosφ = Math.cos(φ);
        let sinλ = Math.sin(λ), cosλ = Math.cos(λ);

        let eSq = 2 * f - f * f;                      // 1st eccentricity squared ≡ (a²-b²)/a²
        let ν = a / Math.sqrt(1 - eSq * sinφ * sinφ); // radius of curvature in prime vertical

        let x = (ν + h) * cosφ * cosλ;
        let y = (ν + h) * cosφ * sinλ;
        let z = (ν * (1 - eSq) + h) * sinφ;

        let point = new Vector3d(x, y, z);

        return point;
    }


    /**
     * Converts ‘this’ (geocentric) cartesian (x/y/z) point to (ellipsoidal geodetic) latitude/longitude
     * coordinates on specified datum.
     *
     * Uses Bowring’s (1985) formulation for μm precision in concise form.
     *
     * @param {LatLon.datum.transform} datum - Datum to use when converting point.
     */
    private static toLatLonE(vector: Vector3d, datum: any) {
        let x = vector.x, y = vector.y, z = vector.z;
        let a = datum.ellipsoid.a, b = datum.ellipsoid.b, f = datum.ellipsoid.f;

        let e2 = 2 * f - f * f;   // 1st eccentricity squared ≡ (a²-b²)/a²
        let ε2 = e2 / (1 - e2); // 2nd eccentricity squared ≡ (a²-b²)/b²
        let p = Math.sqrt(x * x + y * y); // distance from minor axis
        let R = Math.sqrt(p * p + z * z); // polar radius

        // parametric latitude (Bowring eqn 17, replacing tanβ = z·a / p·b)
        let tanβ = (b * z) / (a * p) * (1 + ε2 * b / R);
        let sinβ = tanβ / Math.sqrt(1 + tanβ * tanβ);
        let cosβ = sinβ / tanβ;

        // geodetic latitude (Bowring eqn 18: tanφ = z+ε²bsin³β / p−e²cos³β)
        let φ = isNaN(cosβ) ? 0 : Math.atan2(z + ε2 * b * sinβ * sinβ * sinβ, p - e2 * a * cosβ * cosβ * cosβ);

        // longitude
        let λ = Math.atan2(y, x);

        // height above ellipsoid (Bowring eqn 7) [not currently used]
        let sinφ = Math.sin(φ), cosφ = Math.cos(φ);
        let ν = a / Math.sqrt(1 - e2 * sinφ * sinφ); // length of the normal terminated by the minor axis
        let h = p * cosφ + z * sinφ - (a * a / ν);

        let point = new LatLon(LatLon.toDegrees(φ), LatLon.toDegrees(λ), datum);

        return point;
    }


    /** Extend Number object with method to convert numeric degrees to radians */
    private static toRadians(value: number): number {
        return value * Math.PI / 180;
    }

    private static toDegrees(value: number): number {
        return value * 180 / Math.PI;
    }

    /**
     * Returns the distance between ‘this’ point and destination point along a geodesic, using Vincenty
     * inverse solution.
     *
     * Note: the datum used is of ‘this’ point; distance is on the surface of the ellipsoid (height is
     * ignored).
     *
     * @param   {LatLon} point - Latitude/longitude of destination point.
     * @returns (Number} Distance in metres between points or NaN if failed to converge.
     *
     * @example
     *   let p1 = new LatLon(50.06632, -5.71475);
     *   let p2 = new LatLon(58.64402, -3.07009);
     *   let d = p1.distanceTo(p2); // 969,954.166 m
     */
    public distanceTo(point: LatLon) {
        if (!(point instanceof LatLon)) throw new TypeError('point is not LatLon object');

        try {
            return Number(this.inverse(point).distance.toFixed(3)); // round to 1mm precision
        } catch (e) {
            return NaN; // failed to converge
        }
    }


    /**
     * Returns the initial bearing (forward azimuth) to travel along a geodesic from ‘this’ point to the
     * specified point, using Vincenty inverse solution.
     *
     * Note: the datum used is of ‘this’ point.
     *
     * @param   {LatLon} point - Latitude/longitude of destination point.
     * @returns {number}  initial Bearing in degrees from north (0°..360°) or NaN if failed to converge.
     *
     * @example
     *   let p1 = new LatLon(50.06632, -5.71475);
     *   let p2 = new LatLon(58.64402, -3.07009);
     *   let b1 = p1.initialBearingTo(p2); // 9.1419°
     */
    public initialBearingTo(point: LatLon) {
        if (!(point instanceof LatLon)) throw new TypeError('point is not LatLon object');

        try {
            return Number(this.inverse(point).initialBearing.toFixed(7)); // round to 0.001″ precision
        } catch (e) {
            return NaN; // failed to converge
        }
    }


    /**
     * Returns the final bearing (reverse azimuth) having travelled along a geodesic from ‘this’ point
     * to the specified point, using Vincenty inverse solution.
     *
     * Note: the datum used is of ‘this’ point.
     *
     * @param   {LatLon} point - Latitude/longitude of destination point.
     * @returns {number}  Initial bearing in degrees from north (0°..360°) or NaN if failed to converge.
     *
     * @example
     *   let p1 = new LatLon(50.06632, -5.71475);
     *   let p2 = new LatLon(58.64402, -3.07009);
     *   let b2 = p1.finalBearingTo(p2); // 11.2972°
     */
    public finalBearingTo(point: LatLon) {
        if (!(point instanceof LatLon)) throw new TypeError('point is not LatLon object');

        try {
            return Number(this.inverse(point).finalBearing.toFixed(7)); // round to 0.001″ precision
        } catch (e) {
            return NaN; // failed to converge
        }
    }


    /**
     * Returns the destination point having travelled the given distance along a geodesic given by
     * initial bearing from ‘this’ point, using Vincenty direct solution.
     *
     * Note: the datum used is of ‘this’ point; distance is on the surface of the ellipsoid (height is
     * ignored).
     *
     * @param   {number} distance - Distance travelled along the geodesic in metres.
     * @param   {number} initialBearing - Initial bearing in degrees from north.
     * @returns {LatLon} Destination point.
     *
     * @example
     *   let p1 = new LatLon(-37.95103, 144.42487);
     *   let p2 = p1.destinationPoint(54972.271, 306.86816); // 37.6528°S, 143.9265°E
     */
    public destinationPoint(distance: number, initialBearing: number): LatLon {
        return this.direct(Number(distance), Number(initialBearing)).point;
    }


    /**
     * Returns the final bearing (reverse azimuth) having travelled along a geodesic given by initial
     * bearing for a given distance from ‘this’ point, using Vincenty direct solution.
     *
     * Note: the datum used is of ‘this’ point; distance is on the surface of the ellipsoid (height is
     * ignored).
     *
     * @param   {number} distance - Distance travelled along the geodesic in metres.
     * @param   {LatLon} initialBearing - Initial bearing in degrees from north.
     * @returns {number} Final bearing in degrees from north (0°..360°).
     *
     * @example
     *   let p1 = new LatLon(-37.95103, 144.42487);
     *   let b2 = p1.finalBearingOn(306.86816, 54972.271); // 307.1736°
     */
    public finalBearingOn(distance: number, initialBearing: LatLon): number {
        return Number(this.direct(Number(distance), Number(initialBearing)).finalBearing.toFixed(7)); // round to 0.001″ precision
    }


    /**
     * Vincenty direct calculation.
     *
     * @private
     * @param   {number} distance - Distance along bearing in metres.
     * @param   {number} initialBearing - Initial bearing in degrees from north.
     * @returns (Object} Object including point (destination point), finalBearing.
     * @throws  {Error}  If formula failed to converge.
     */
    public direct(distance: number, initialBearing: number): any {
        let φ1 = LatLon.toRadians(this.lat), λ1 = LatLon.toRadians(this.lon);
        let α1 = LatLon.toRadians(initialBearing);
        let s = distance;

        let a = this._datum.ellipsoid.a, b = this._datum.ellipsoid.b, f = this._datum.ellipsoid.f;

        let sinα1 = Math.sin(α1);
        let cosα1 = Math.cos(α1);

        let tanU1 = (1 - f) * Math.tan(φ1), cosU1 = 1 / Math.sqrt((1 + tanU1 * tanU1)), sinU1 = tanU1 * cosU1;
        let σ1 = Math.atan2(tanU1, cosα1);
        let sinα = cosU1 * sinα1;
        let cosSqα = 1 - sinα * sinα;
        let uSq = cosSqα * (a * a - b * b) / (b * b);
        let A = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
        let B = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));

        let cos2σM, sinσ, cosσ, Δσ;

        let σ = s / (b * A), σʹ, iterations = 0;
        do {
            cos2σM = Math.cos(2 * σ1 + σ);
            sinσ = Math.sin(σ);
            cosσ = Math.cos(σ);
            Δσ = B * sinσ * (cos2σM + B / 4 * (cosσ * (-1 + 2 * cos2σM * cos2σM) -
                B / 6 * cos2σM * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σM * cos2σM)));
            σʹ = σ;
            σ = s / (b * A) + Δσ;
        } while (Math.abs(σ - σʹ) > 1e-12 && ++iterations < 100);
        if (iterations >= 100) throw new Error('Formula failed to converge'); // not possible!

        let x = sinU1 * sinσ - cosU1 * cosσ * cosα1;
        let φ2 = Math.atan2(sinU1 * cosσ + cosU1 * sinσ * cosα1, (1 - f) * Math.sqrt(sinα * sinα + x * x));
        let λ = Math.atan2(sinσ * sinα1, cosU1 * cosσ - sinU1 * sinσ * cosα1);
        let C = f / 16 * cosSqα * (4 + f * (4 - 3 * cosSqα));
        let L = λ - (1 - C) * f * sinα *
            (σ + C * sinσ * (cos2σM + C * cosσ * (-1 + 2 * cos2σM * cos2σM)));
        let λ2 = (λ1 + L + 3 * Math.PI) % (2 * Math.PI) - Math.PI;  // normalise to -180..+180

        let α2 = Math.atan2(sinα, -x);
        α2 = (α2 + 2 * Math.PI) % (2 * Math.PI); // normalise to 0..360

        return {
            point: new LatLon(LatLon.toDegrees(φ2), LatLon.toDegrees(λ2), this._datum),
            finalBearing: LatLon.toDegrees(α2),
            iterations: iterations,
        };
    }

    /**
     * Vincenty inverse calculation.
     *
     * @private
     * @param   {LatLon} point - Latitude/longitude of destination point.
     * @returns {Object} Object including distance, initialBearing, finalBearing.
     * @throws  {Error}  If λ > π or formula failed to converge.
     */
    public inverse(point: LatLon) {
        let p1 = this, p2 = point;
        if (p1.lon == -180) p1.lon = 180;
        let φ1 = LatLon.toRadians(p1.lat), λ1 = LatLon.toRadians(p1.lon);
        let φ2 = LatLon.toRadians(p2.lat), λ2 = LatLon.toRadians(p2.lon);

        let a = this._datum.ellipsoid.a, b = this._datum.ellipsoid.b, f = this._datum.ellipsoid.f;

        let L = λ2 - λ1;
        let tanU1 = (1 - f) * Math.tan(φ1), cosU1 = 1 / Math.sqrt((1 + tanU1 * tanU1)), sinU1 = tanU1 * cosU1;
        let tanU2 = (1 - f) * Math.tan(φ2), cosU2 = 1 / Math.sqrt((1 + tanU2 * tanU2)), sinU2 = tanU2 * cosU2;

        let sinλ, cosλ, sinSqσ, sinσ = 0, cosσ = 0, σ = 0, sinα, cosSqα = 0, cos2σM = 0, C;

        let λ = L, λʹ, iterations = 0, antimeridian = Math.abs(L) > Math.PI;
        do {
            sinλ = Math.sin(λ);
            cosλ = Math.cos(λ);
            sinSqσ = (cosU2 * sinλ) * (cosU2 * sinλ) + (cosU1 * sinU2 - sinU1 * cosU2 * cosλ) * (cosU1 * sinU2 - sinU1 * cosU2 * cosλ);
            if (sinSqσ == 0) break; // co-incident points
            sinσ = Math.sqrt(sinSqσ);
            cosσ = sinU1 * sinU2 + cosU1 * cosU2 * cosλ;
            σ = Math.atan2(sinσ, cosσ);
            sinα = cosU1 * cosU2 * sinλ / sinσ;
            cosSqα = 1 - sinα * sinα;
            cos2σM = (cosSqα != 0) ? (cosσ - 2 * sinU1 * sinU2 / cosSqα) : 0; // equatorial line: cosSqα=0 (§6)
            C = f / 16 * cosSqα * (4 + f * (4 - 3 * cosSqα));
            λʹ = λ;
            λ = L + (1 - C) * f * sinα * (σ + C * sinσ * (cos2σM + C * cosσ * (-1 + 2 * cos2σM * cos2σM)));
            let iterationCheck = antimeridian ? Math.abs(λ) - Math.PI : Math.abs(λ);
            if (iterationCheck > Math.PI) throw new Error('λ > π');
        } while (Math.abs(λ - λʹ) > 1e-12 && ++iterations < 1000);
        if (iterations >= 1000) throw new Error('Formula failed to converge');

        let uSq = cosSqα * (a * a - b * b) / (b * b);
        let A = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
        let B = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
        let Δσ = B * sinσ * (cos2σM + B / 4 * (cosσ * (-1 + 2 * cos2σM * cos2σM) -
            B / 6 * cos2σM * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σM * cos2σM)));

        let s = b * A * (σ - Δσ);

        let α1 = Math.atan2(cosU2 * sinλ, cosU1 * sinU2 - sinU1 * cosU2 * cosλ);
        let α2 = Math.atan2(cosU1 * sinλ, -sinU1 * cosU2 + cosU1 * sinU2 * cosλ);

        α1 = (α1 + 2 * Math.PI) % (2 * Math.PI); // normalise to 0..360
        α2 = (α2 + 2 * Math.PI) % (2 * Math.PI); // normalise to 0..360

        return {
            distance: s,
            initialBearing: s == 0 ? NaN : LatLon.toDegrees(α1),
            finalBearing: s == 0 ? NaN : LatLon.toDegrees(α2),
            iterations: iterations,
        };
    }

    /**
     * Returns a string representation of ‘this’ point, formatted as degrees, degrees+minutes, or
     * degrees+minutes+seconds.
     *
     * @param   {string} [format=dms] - Format point as 'd', 'dm', 'dms'.
     * @param   {number} [dp=0|2|4] - Number of decimal places to use - default 0 for dms, 2 for dm, 4 for d.
     * @returns {string} Comma-separated latitude/longitude.
     */
    public toString(format, dp) {
        return Dms.toLat(this.lat, format, dp) + ', ' + Dms.toLon(this.lon, format, dp);
    }
}
